import { useEffect, useState } from 'react';
import { statusLabel } from '@/components/ui/primitives';
import { useReducedMotion } from '@/lib/motion';
import { cn, display } from '@/lib/utils';
import { AnimatedNumber } from '@/features/dashboard/dashboard-ui';
import { BOARD_STATUSES, occupancyPercent, ringSegments, type StatusCounts } from './bed-status';
import { STATUS_BAR, STATUS_LOOK } from './bed-board-look';

const RING_RADIUS = 50;
const RING_STROKE = 12;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;
const RING_GAP = 2.5;

function percentText(value: number): string {
  try {
    return new Intl.NumberFormat(display.locale, { style: 'percent', maximumFractionDigits: 0 }).format(value / 100);
  } catch {
    return `${Math.round(value)}%`;
  }
}

export function OccupancyRing({ counts, label, className }: { counts: StatusCounts; label: string; className?: string }) {
  const reduced = useReducedMotion();
  const [drawn, setDrawn] = useState(reduced);
  useEffect(() => {
    if (drawn) return undefined;
    const frame = requestAnimationFrame(() => setDrawn(true));
    return () => cancelAnimationFrame(frame);
  }, [drawn]);

  const segments = ringSegments(counts, RING_CIRCUMFERENCE, RING_GAP);
  const percent = occupancyPercent(counts);

  return (
    <div className={cn('relative grid aspect-square w-40 shrink-0 place-items-center sm:w-44', className)}>
      <svg viewBox="0 0 120 120" className="absolute inset-0 h-full w-full -rotate-90" role="img" aria-label={label}>
        <circle cx="60" cy="60" r={RING_RADIUS} fill="none" strokeWidth={RING_STROKE} className="stroke-muted" />
        {segments.map((segment) => (
          <circle
            key={segment.status}
            cx="60"
            cy="60"
            r={RING_RADIUS}
            fill="none"
            strokeWidth={RING_STROKE}
            strokeDasharray={`${drawn ? segment.length : 0} ${RING_CIRCUMFERENCE}`}
            strokeDashoffset={drawn ? -segment.offset : 0}
            className={cn(
              STATUS_LOOK[segment.status].stroke,
              'transition-[stroke-dasharray,stroke-dashoffset] duration-1000 ease-out motion-reduce:transition-none',
            )}
          >
            <title>{`${statusLabel(segment.status)}: ${segment.count}`}</title>
          </circle>
        ))}
      </svg>
      <div className="relative flex flex-col items-center text-center">
        <span className="text-3xl font-semibold leading-none tracking-tight">
          <AnimatedNumber value={percent} format={percentText} />
        </span>
        <span className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      </div>
    </div>
  );
}

export function StatusMiniBar({ counts, className }: { counts: StatusCounts; className?: string }) {
  return (
    <span className={cn('flex h-1.5 w-full gap-px overflow-hidden rounded-full bg-muted', className)} aria-hidden>
      {BOARD_STATUSES.filter((status) => counts[status] > 0).map((status) => (
        <span
          key={status}
          className={cn('h-full transition-[flex-grow] duration-700 ease-out', STATUS_BAR[status])}
          style={{ flexGrow: counts[status], flexBasis: 0 }}
        />
      ))}
    </span>
  );
}

export function BedArt({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 96 56" className={cn('flip-rtl h-12 w-20', className)} aria-hidden>
      <ellipse cx="48" cy="52" rx="38" ry="3" className="fill-muted" />
      <rect x="8" y="10" width="6" height="34" rx="2" fill="currentColor" opacity="0.35" />
      <rect x="82" y="22" width="5" height="22" rx="2" fill="currentColor" opacity="0.35" />
      <rect x="14" y="40" width="3" height="7" rx="1" fill="currentColor" opacity="0.45" />
      <rect x="79" y="40" width="3" height="7" rx="1" fill="currentColor" opacity="0.45" />
      <circle cx="15.5" cy="48.5" r="2.5" fill="currentColor" opacity="0.55" />
      <circle cx="80.5" cy="48.5" r="2.5" fill="currentColor" opacity="0.55" />
      <rect x="10" y="34" width="76" height="6" rx="2" fill="currentColor" opacity="0.5" />
      <rect x="14" y="26" width="68" height="9" rx="3" className="fill-card" stroke="currentColor" strokeOpacity="0.4" strokeWidth="1.2" />
      <rect x="17" y="19" width="16" height="8" rx="4" fill="currentColor" opacity="0.25" />
      <rect x="36" y="24" width="46" height="11" rx="3" fill="currentColor" opacity="0.7" />
      <path d="M36 28h46" strokeOpacity="0.6" strokeWidth="1.2" className="stroke-card" />
    </svg>
  );
}

export function EmptyWardIllustration({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 160 112" className={cn('h-28 w-40 text-primary', className)} aria-hidden>
      <ellipse cx="80" cy="102" rx="60" ry="6" className="fill-muted" />
      <rect x="18" y="14" width="124" height="70" rx="10" className="fill-card stroke-border" strokeWidth="2" strokeDasharray="6 5" />
      <circle cx="136" cy="12" r="5" fill="currentColor" opacity="0.2" />
      <circle cx="22" cy="96" r="3.5" fill="currentColor" opacity="0.18" />
      <g transform="translate(32 30)">
        <rect x="0" y="4" width="8" height="40" rx="2.5" fill="currentColor" opacity="0.3" />
        <rect x="88" y="18" width="7" height="26" rx="2.5" fill="currentColor" opacity="0.3" />
        <rect x="2" y="34" width="92" height="7" rx="2.5" fill="currentColor" opacity="0.4" />
        <rect x="6" y="24" width="84" height="11" rx="4" className="fill-card" stroke="currentColor" strokeOpacity="0.35" strokeWidth="1.5" />
        <rect x="10" y="15" width="20" height="10" rx="5" fill="currentColor" opacity="0.2" />
        <rect x="6" y="41" width="4" height="8" rx="1.5" fill="currentColor" opacity="0.4" />
        <rect x="86" y="41" width="4" height="8" rx="1.5" fill="currentColor" opacity="0.4" />
      </g>
      <circle cx="118" cy="40" r="11" fill="currentColor" opacity="0.12" />
      <path d="M118 34v12M112 40h12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

export function SexGlyph({ sex, label, className }: { sex: string; label: string; className?: string }) {
  return (
    <svg
      viewBox="0 0 12 12"
      className={cn('h-3 w-3 shrink-0', className)}
      role="img"
      aria-label={label}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <title>{label}</title>
      {sex === 'male' && (
        <>
          <circle cx="4.8" cy="7.2" r="3.3" />
          <path d="M7.2 4.8 10.8 1.2M8 1.2h2.8V4" />
        </>
      )}
      {sex === 'female' && (
        <>
          <circle cx="6" cy="4.5" r="3.3" />
          <path d="M6 7.8v3.6M4.2 9.8h3.6" />
        </>
      )}
      {sex !== 'male' && sex !== 'female' && <circle cx="6" cy="6" r="3.6" />}
    </svg>
  );
}
